import { createContext, useContext, type ReactNode } from 'react';
import { useSupportMessenger, type UseSupportMessenger } from '@/hooks/useSupportMessenger';

const noop: UseSupportMessenger = {
  isActive: false,
  openNewMessage: async () => false,
  canOfferGuestHelp: false,
  bootGuestSession: () => {},
};

const SupportMessengerContext = createContext<UseSupportMessenger>(noop);

/**
 * Mounted once near the app root (inside the router, so the hook can read the current
 * route). Boots/shuts down the support messenger as auth/route change, and exposes
 * `isActive` + `openNewMessage` to any descendant — e.g. the tenant<->owner conversation
 * header's "Need help?" escalation button, and `canOfferGuestHelp` + `bootGuestSession` for
 * the logged-out guest button on allow-listed pages (e.g. /claim/:token) — none of them need to
 * know the widget's internals.
 */
export function SupportMessengerProvider({ children }: { children: ReactNode }) {
  const value = useSupportMessenger();
  return (
    <SupportMessengerContext.Provider value={value}>{children}</SupportMessengerContext.Provider>
  );
}

/** Safe to call outside the provider (returns the no-op default) so a stray import never crashes. */
export function useSupportMessengerContext(): UseSupportMessenger {
  return useContext(SupportMessengerContext);
}
